import { useMemo, useState } from 'react'
import { Button, Card, CollapsibleSection, KeyValueList, PillTone, StatusPill } from '../../../ui'
import { Fact, ReplyDetail, formatDateTime, formatFactValue, humanise, pence } from './shared'
import styles from './styles.module.scss'

/** Plain-English verdict for one extracted fact, for the support team. */
const factCheck = (f: Fact, linked: boolean): { tone: PillTone; text: string; title?: string } | null => {
    if (f.decidedBy) return { tone: 'good', text: `Confirmed by ${f.decidedBy}` }
    if (f.validation === 'disputed') return { tone: 'warn', text: 'Unclear — check the email', title: 'The email was read more than once and the readings did not agree. Pick the right one below or check the email text.' }
    if (f.comparison === 'agree') return { tone: 'good', text: 'Matches our record' }
    if (f.comparison === 'disagree') return { tone: 'bad', text: 'Differs from our record', title: 'The lender quoted something different from what we hold for this client. Check which is right.' }
    if (f.comparison === 'new' && linked) return { tone: 'neutral', text: f.isIdentifier ? 'Not on our record' : 'New information' }
    return null
}

const SNAKE = /^[a-z]+(_[a-z]+)+$/
const displayValue = (f: Fact) => {
    const v = formatFactValue(f)
    return SNAKE.test(v) ? humanise(v) : v
}

const distinctReadings = (f: Fact): string[] => {
    const values = (f.candidates || []).map((c) => (c.value === null || c.value === undefined ? '' : String(c.value))).filter((v) => v && v !== f.valueRaw)
    return Array.from(new Set(values))
}

/**
 * "What the lender told us": each data point found in the email against what
 * we hold for the client. Model names and voting stay out of sight; a
 * disputed reading offers the alternatives so the team can pick the right one.
 */
export const ExtractedDataCard = ({ detail, busy, onUseValue }: { detail: ReplyDetail; busy: boolean; onUseValue: (factId: number, valueRaw: string) => void }) => {
    const linked = detail.linkState === 'linked'
    const groups = useMemo(() => {
        const out: Record<string, Fact[]> = {}
        for (const f of detail.facts) (out[f.group] ||= []).push(f)
        return Object.entries(out)
    }, [detail.facts])

    if (groups.length === 0) return null

    const differs = detail.facts.filter((f) => f.comparison === 'disagree' && !f.decidedBy).length
    const unclear = detail.facts.filter((f) => f.validation === 'disputed' && !f.decidedBy).length

    return (
        <Card
            padding="sm"
            title="What the lender told us"
            subtitle={linked ? 'Details found in this email, checked against the client record we hold.' : 'Details found in this email. Link it to a claim and they will be checked against the client record.'}
            actions={
                <div className={styles.chips}>
                    {differs > 0 && (
                        <StatusPill size="sm" tone="bad">
                            {differs} differ{differs === 1 ? 's' : ''} from record
                        </StatusPill>
                    )}
                    {unclear > 0 && (
                        <StatusPill size="sm" tone="warn">
                            {unclear} unclear
                        </StatusPill>
                    )}
                    {differs === 0 && unclear === 0 && linked && (
                        <StatusPill size="sm" tone="good">
                            Nothing to query
                        </StatusPill>
                    )}
                </div>
            }
        >
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Detail</th>
                        <th>In this email</th>
                        {linked && <th>Our record</th>}
                        <th>Check</th>
                    </tr>
                </thead>
                {groups.map(([group, facts]) => (
                    <tbody key={group}>
                        <tr className={styles.groupRow}>
                            <td colSpan={linked ? 4 : 3}>{humanise(group)}</td>
                        </tr>
                        {facts.map((f) => {
                            const check = factCheck(f, linked)
                            const readings = f.validation === 'disputed' && !f.decidedBy ? distinctReadings(f) : []
                            return (
                                <tr key={f.id} className={f.validation === 'disputed' && !f.decidedBy ? styles.rowDisputed : f.comparison === 'disagree' && !f.decidedBy ? styles.rowDiffers : undefined}>
                                    <td className={styles.factLabel}>{f.label}</td>
                                    <td>
                                        <span className={styles.factValue}>{displayValue(f)}</span>
                                        {f.sourceSpan && (
                                            <div className={styles.sourceSpan} title="The words in the email this was taken from">
                                                “{f.sourceSpan}”
                                            </div>
                                        )}
                                        {readings.length > 0 && (
                                            <div className={styles.readings}>
                                                <span className={styles.mutedInline}>Could also be:</span>
                                                {readings.map((v) => (
                                                    <Button key={v} size="sm" variant="link" disabled={busy} onClick={() => onUseValue(f.id, v)} title="Use this reading instead">
                                                        {v}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    {linked && <td className={styles.homeValue}>{f.homeValue ?? <span className={styles.mutedInline}>—</span>}</td>}
                                    <td className={styles.tdChips}>
                                        {check && (
                                            <StatusPill size="sm" tone={check.tone} title={check.title}>
                                                {check.text}
                                            </StatusPill>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                ))}
            </table>
        </Card>
    )
}

/**
 * Everything a case handler does not need day to day: how the email was read,
 * which rules fired, model runs and cost, raw headers and attachment parsing.
 * Collapsed by default.
 */
export const TechnicalDetails = ({ detail, busy, onRematch, onReextract, onDownloadRaw }: { detail: ReplyDetail; busy: string | null; onRematch: () => void; onReextract: () => void; onDownloadRaw: () => void }) => {
    const [open, setOpen] = useState(false)
    const runs = detail.inferenceRuns
    const totalCost = runs.reduce((a, r) => a + (r.costPence || 0), 0)

    return (
        <CollapsibleSection
            title="Technical details"
            meta={runs.length ? `${runs.length} model run${runs.length === 1 ? '' : 's'} · ${pence(totalCost)}` : 'headers, matching, attachments'}
            variant="panel"
            open={open}
            onToggle={setOpen}
            actions={
                <div className={styles.actionRow}>
                    <Button size="sm" disabled={busy !== null} loading={busy === 'Rematch'} title="Run the case-matching rules again" onClick={onRematch}>
                        Re-check link
                    </Button>
                    <Button size="sm" disabled={busy !== null} loading={busy === 'Extract'} title="Read the email again from scratch (category, summary and details)" onClick={onReextract}>
                        Re-read email
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onDownloadRaw} disabled={busy !== null || !detail.rawS3Key} loading={busy === 'raw'} title="Download the original .eml file">
                        .eml
                    </Button>
                </div>
            }
        >
            <div className={styles.stack}>
                <KeyValueList
                    columns
                    items={[
                        { label: 'From', value: detail.fromEmail, mono: true },
                        { label: 'To', value: detail.toEmail, mono: true },
                        { label: 'Message-ID', value: detail.rfcMessageId, mono: true },
                        { label: 'In-Reply-To', value: detail.inReplyTo, mono: true },
                        { label: 'Party', value: humanise(detail.partyType) },
                        { label: 'Matched by', value: detail.matchMethod ? `${humanise(detail.matchMethod)}${detail.matchedBy ? ` · ${detail.matchedBy}` : ''}${detail.matchConfidence != null ? ` · ${detail.matchConfidence}%` : ''}` : null },
                        { label: 'Not linked because', value: detail.linkState !== 'linked' ? humanise(detail.unlinkedReason) : null },
                        { label: 'Read state', value: `${detail.extractionState}${detail.extractionError ? ` — ${detail.extractionError}` : ''}` },
                        {
                            label: 'Category set by',
                            value: detail.classificationSource ? `${detail.classificationSource}${detail.classifiedBy ? ` (${detail.classifiedBy})` : ''}${detail.classificationConfidence != null ? ` · confidence ${detail.classificationConfidence}` : ''}${detail.classificationPayload?.validation || detail.extraction?.classificationValidation ? ` · ${detail.classificationPayload?.validation || detail.extraction?.classificationValidation}` : ''}` : null,
                        },
                        {
                            label: 'Extraction',
                            value: detail.extraction ? `${detail.extraction.voting || 'single'} · ${detail.extraction.consensusState || '—'}${detail.extraction.disputedFieldCount ? ` · ${detail.extraction.disputedFieldCount} disputed` : ''} · ${pence(detail.extraction.costPence)} · prompt ${detail.extraction.promptVersion} · ${formatDateTime(detail.extraction.extractedAt)}` : null,
                        },
                        {
                            label: 'Attachments',
                            value: detail.attachments.length ? detail.attachments.map((a) => `${a.filename}${a.pageCount ? ` (${a.pageCount}p)` : ''}${a.rejectedReason ? ` ✕ ${a.rejectedReason}` : a.hasExtractedText ? ' ✓ text' : ''}`).join(', ') : null,
                        },
                        { label: 'Also seen in', value: detail.locations?.length > 1 ? detail.locations.map((l) => `${l.mailbox}${l.folder ? ` / ${l.folder}` : ''}`).join(', ') : null },
                    ]}
                />

                {detail.classificationPayload?.candidates && detail.classificationPayload.candidates.length > 1 && (
                    <div className={styles.chips}>
                        <span className={styles.mutedInline}>Category votes:</span>
                        {detail.classificationPayload.candidates.map((c) => (
                            <StatusPill key={c.runId} size="sm" title={c.role}>
                                {c.modelKey}: {humanise(String(c.value))}
                            </StatusPill>
                        ))}
                    </div>
                )}

                {detail.ruleHits.length > 0 && (
                    <div className={styles.chips}>
                        <span className={styles.mutedInline}>Rules:</span>
                        {detail.ruleHits.map((h) => (
                            <StatusPill key={h.id} size="sm" tone={h.wasShadow ? 'neutral' : 'warn'} title={h.snippet || undefined}>
                                {h.wasShadow ? '(shadow) ' : ''}
                                {h.ruleName} → {humanise(h.classification)}
                            </StatusPill>
                        ))}
                    </div>
                )}

                {detail.facts.some((f) => f.candidates && f.candidates.length > 0) && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Detail</th>
                                <th>Chosen</th>
                                <th>Readings</th>
                                <th>Agreement</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.facts.map((f) => (
                                <tr key={f.id}>
                                    <td className={styles.factLabel}>{f.label}</td>
                                    <td className={styles.mono}>{f.valueRaw}</td>
                                    <td className={styles.tdModels}>
                                        {(f.candidates || []).map((c) => (
                                            <span key={c.runId} className={styles.modelVote} title={c.role}>
                                                <span className={styles.modelName}>{c.modelKey}</span> {c.value === null || c.value === undefined ? '∅' : String(c.value)}
                                            </span>
                                        ))}
                                    </td>
                                    <td>
                                        <StatusPill size="sm" tone={f.validation === 'disputed' ? 'bad' : f.validation === 'majority' ? 'warn' : 'good'}>
                                            {f.validation}
                                            {f.confidence != null && f.confidence < 100 ? ` · ${f.confidence}%` : ''}
                                        </StatusPill>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {runs.length > 0 && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Model</th>
                                <th>Status</th>
                                <th>Tokens</th>
                                <th>Cost</th>
                                <th>Latency</th>
                                <th>When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.role}</td>
                                    <td>
                                        <span className={styles.modelName}>{r.modelKey}</span> <span className={styles.mutedInline}>({r.provider})</span>
                                    </td>
                                    <td>
                                        <StatusPill size="sm" tone={r.status === 'ok' || r.status === 'cached' ? 'good' : 'bad'} title={r.error || undefined}>
                                            {r.status}
                                        </StatusPill>
                                    </td>
                                    <td className={styles.mono}>
                                        {r.inputTokens ?? '—'} / {r.outputTokens ?? '—'}
                                    </td>
                                    <td>{pence(r.costPence)}</td>
                                    <td>{r.latencyMs != null ? `${(r.latencyMs / 1000).toFixed(1)}s` : '—'}</td>
                                    <td>{formatDateTime(r.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </CollapsibleSection>
    )
}
